import * as pdfjsLib from "pdfjs-dist/legacy/build/pdf.mjs";
import pdfWorker from "pdfjs-dist/legacy/build/pdf.worker.mjs?url";

pdfjsLib.GlobalWorkerOptions.workerSrc = pdfWorker;

const MONTHS = {
  gennaio: "01", febbraio: "02", marzo: "03", aprile: "04",
  maggio: "05", giugno: "06", luglio: "07", agosto: "08",
  settembre: "09", ottobre: "10", novembre: "11", dicembre: "12",
};

function isoDate(value) {
  if (!value) return "";
  const clean = value.trim().toLowerCase();
  let match = clean.match(/\b(\d{1,2})[\/.\-](\d{1,2})[\/.\-](\d{4})\b/);
  if (match) return `${match[3]}-${match[2].padStart(2, "0")}-${match[1].padStart(2, "0")}`;
  match = clean.match(/\b(\d{4})[\/.\-](\d{1,2})[\/.\-](\d{1,2})\b/);
  if (match) return `${match[1]}-${match[2].padStart(2, "0")}-${match[3].padStart(2, "0")}`;
  match = clean.match(/\b(\d{1,2})\s+(gennaio|febbraio|marzo|aprile|maggio|giugno|luglio|agosto|settembre|ottobre|novembre|dicembre)\s+(\d{4})\b/);
  if (match) return `${match[3]}-${MONTHS[match[2]]}-${match[1].padStart(2, "0")}`;
  return "";
}

function parseEuro(value) {
  if (!value) return null;
  const normalized = value
    .replace(/[€\s]/g, "")
    .replace(/\.(?=\d{3}(?:\D|$))/g, "")
    .replace(",", ".")
    .replace(/[^\d.-]/g, "");
  const number = Number.parseFloat(normalized);
  return Number.isFinite(number) ? Math.round(number * 100) : null;
}

function valueNearLabel(text, labels, valuePattern, distance = 100) {
  for (const label of labels) {
    const index = text.toLowerCase().indexOf(label.toLowerCase());
    if (index >= 0) {
      const window = text.slice(index + label.length, index + label.length + distance);
      const match = window.match(valuePattern);
      if (match) return match[1] || match[0];
    }
  }
  return "";
}

function parseText(text, fileName) {
  const normalized = text.replace(/\s+/g, " ").trim();
  const datePattern = /((?:\d{1,2}[\/.\-]\d{1,2}[\/.\-]\d{4})|(?:\d{4}[\/.\-]\d{1,2}[\/.\-]\d{1,2}))/;
  const amountPattern = /(?:€\s*)?((?:\d{1,3}(?:\.\d{3})*|\d+)[,.]\d{2})/;

  let dueRaw = valueNearLabel(normalized, ["data scadenza", "scadenza pagamento", "scadenza"], datePattern, 80);
  let issueRaw = valueNearLabel(normalized, ["data documento", "data fattura", "emessa il"], datePattern, 70);
  let amountRaw = valueNearLabel(
    normalized,
    ["totale documento", "totale fattura", "importo pagamento", "totale da pagare", "importo dovuto"],
    amountPattern,
    100,
  );
  const invoiceNumber = valueNearLabel(
    normalized,
    ["numero documento", "numero fattura", "fattura n.", "fattura n°", "fattura nr"],
    /[:\s]*([A-Z0-9][A-Z0-9_\-/]{1,30})/i,
    70,
  );

  const allAmounts = [...normalized.matchAll(/(?:€\s*)?((?:\d{1,3}(?:\.\d{3})*|\d+)[,.]\d{2})/g)]
    .map((match) => ({ raw: match[1], cents: parseEuro(match[1]) }))
    .filter((item) => item.cents !== null);
  if (!amountRaw && allAmounts.length) {
    amountRaw = allAmounts.sort((a, b) => b.cents - a.cents)[0].raw;
  }

  const nameGuess = normalized.match(/(?:denominazione|ragione sociale)[:\s]+(.{3,70}?)(?=\s(?:regime fiscale|indirizzo|codice fiscale|partita iva|p\.\s*iva|$))/i);
  const confidenceParts = [isoDate(dueRaw), parseEuro(amountRaw), invoiceNumber].filter(Boolean).length;

  return {
    invoiceNumber: invoiceNumber || fileName.replace(/\.[^.]+$/, ""),
    supplierName: nameGuess?.[1]?.trim() || "",
    issueDate: isoDate(issueRaw),
    dueDate: isoDate(dueRaw),
    amountCents: parseEuro(amountRaw),
    currency: "EUR",
    extractionConfidence: Math.round((confidenceParts / 3) * 100),
    parseSource: "pdf-text",
  };
}

function firstXmlText(doc, localName) {
  const node = [...doc.getElementsByTagName("*")].find((item) => item.localName === localName);
  return node?.textContent?.trim() || "";
}

async function extractXml(file) {
  const text = await file.text();
  const doc = new DOMParser().parseFromString(text, "application/xml");
  if (doc.querySelector("parsererror")) throw new Error("Il file XML non è valido.");
  const denomination = firstXmlText(doc, "Denominazione");
  const firstName = firstXmlText(doc, "Nome");
  const lastName = firstXmlText(doc, "Cognome");
  return {
    invoiceNumber: firstXmlText(doc, "Numero") || file.name.replace(/\.[^.]+$/, ""),
    supplierName: denomination || [firstName, lastName].filter(Boolean).join(" "),
    issueDate: isoDate(firstXmlText(doc, "Data")),
    dueDate: isoDate(firstXmlText(doc, "DataScadenzaPagamento")),
    amountCents: parseEuro(firstXmlText(doc, "ImportoPagamento") || firstXmlText(doc, "ImportoTotaleDocumento")),
    currency: firstXmlText(doc, "Divisa") || "EUR",
    extractionConfidence: 100,
    parseSource: "fattura-xml",
  };
}

async function extractPdf(file) {
  const bytes = new Uint8Array(await file.arrayBuffer());
  const pdf = await pdfjsLib.getDocument({ data: bytes }).promise;
  const pageTexts = [];
  for (let pageNumber = 1; pageNumber <= Math.min(pdf.numPages, 12); pageNumber += 1) {
    const page = await pdf.getPage(pageNumber);
    const content = await page.getTextContent();
    pageTexts.push(content.items.map((item) => item.str).join(" "));
  }
  const text = pageTexts.join(" \n ");
  if (!text.trim()) throw new Error("Il PDF non contiene testo selezionabile. Inserisci i dati manualmente.");
  return parseText(text, file.name);
}

export async function extractInvoice(file) {
  const lowerName = file.name.toLowerCase();
  if (file.type === "application/pdf" || lowerName.endsWith(".pdf")) return extractPdf(file);
  if (file.type.includes("xml") || lowerName.endsWith(".xml") || lowerName.endsWith(".p7m")) return extractXml(file);
  throw new Error("Formato non supportato. Usa PDF o XML.");
}
